import { Input } from '@dynamika-shared/ui/Input/Input';
import { Switch } from '@dynamika-shared/ui/Switch/Switch';
import { CodeEditor } from 'src/pages/ClinicalTrial/Readings/ReadingInventoryItemForm/CodeEditor';
import { useInventoryFormHandler } from '@hooks/useInventoryFormHandler';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { useMemo } from 'react';
import { runToast } from 'src/store-redux/tools';

const COMMIT_BASE_URL =
  'https://bitbucket.org/image-analysis-group/d7-templates/commits';

export const ScoringForm = () => {
  const { onChange, onBlur, watch, errors } = useInventoryFormHandler();

  const schemaInfo = useMemo(() => {
    const schema = watch('schema');
    if (!schema) return null;
    try {
      const data = typeof schema === 'string' ? JSON.parse(schema) : schema;
      return {
        generatedAt: data.$generatedAt,
        formCode: data.$formCode,
        commit: data.$commit,
      };
    } catch (error) {
      return null;
    }
  }, [watch('schema')]);

  const fetchLatestSchema = (scoringFormCode: string) => async () => {
    try {
      const response = await fetch(
        `/forms/js/${scoringFormCode}/${scoringFormCode}.schema.json`
      );
      const data = await response.json();
      onChange('schema', data);

      runToast(
        'info',
        `${data.$generatedAt}, ${data.$formCode}, commit: ${data.$commit}`
      );
    } catch (error) {
      console.error('Error fetching the latest schema:', error);

      runToast(
        'error',
        (error as Error).message,
        'Failed to fetch the latest schema'
      );
    }
  };

  return (
    <>
      <div className={'flex gap-2'}>
        <Input
          required
          value={watch('extractor')}
          name={'extractor'}
          onChange={e => onChange('extractor', e.target.value)}
          onBlur={e => onBlur('extractor', e.target.value)}
          data-testid={'item-extractor'}
          errors={errors.extractor?.message as string}
          label={'Extractor'}
          placeholder={'Extractor'}
          classNameInputWrapper='max-w-full'
          maxLength={255}
        />
      </div>
      <div className={'flex'}>
        <Switch
          label={'Viewer Embedded:'}
          name={'viewerEmbedded'}
          checked={watch('viewerEmbedded')}
          onChange={e => onChange('viewerEmbedded', e)}
          data-testid={'viewerEmbedded'}
          className={'mt-2.5'}
        />
        <Switch
          label={'Sign Required:'}
          name={'signRequired'}
          checked={watch('signRequired')}
          onChange={e => onChange('signRequired', e)}
          data-testid={'signRequired'}
          className={'mt-2.5'}
        />
        <Switch
          label={'Grouped:'}
          name={'grouped'}
          checked={watch('grouped')}
          onChange={e => onChange('grouped', e)}
          data-testid={'grouped'}
          className={'mt-2.5'}
        />
      </div>
      <div className='w-full '>
        <Button
          onClick={fetchLatestSchema(watch('code'))}
          type='primary'
          size='small'
        >
          Fetch Latest Schema
        </Button>
        <div className='w-full text-arsenic font-semibold mt-1'>
          Current:{' '}
          {schemaInfo && (
            <>
              {schemaInfo.generatedAt} | {schemaInfo.formCode} |{' '}
              {schemaInfo.commit && (
                <a
                  href={`${COMMIT_BASE_URL}/${schemaInfo.commit}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='underline'
                >
                  {schemaInfo.commit}
                </a>
              )}
            </>
          )}
        </div>
      </div>
      <CodeEditor
        classNameWrapper={'col-span-2'}
        title={'Schema'}
        language={'json'}
        value={watch('schema')}
        onChange={value => onChange('schema', value)}
      />
    </>
  );
};
